import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconDeleteTableGroups, iconEditTableGroups } from '../../assets';
import { useDebounce } from '../../hooks/useDebounce';
import { useTheme } from '../../theme';
import { SearchToolbar } from '../SearchToolbar';
import type { FetchGroupsResponse, GroupsItem, GroupsProps } from './types';

const DEBOUNCE_MS = 400;
const DEFAULT_PAGE_SIZE = 10;
const ACTION_ICON_SIZE = 36;

export function Groups({
  fetchGroups,
  title,
  subtitle,
  searchPlaceholder,
  createButtonLabel,
  onCreateGroup,
  onEdit,
  onDelete,
  searchDebounceMs = DEBOUNCE_MS,
}: GroupsProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const titleText = title ?? t('groups.title');
  const subtitleText = subtitle ?? t('groups.subtitle');
  const searchPlaceholderText =
    searchPlaceholder ?? t('groups.search_placeholder');
  const createButtonLabelText = createButtonLabel ?? t('groups.create_button');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, searchDebounceMs);
  const [data, setData] = useState<GroupsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res: FetchGroupsResponse = await fetchGroups({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setData(res.data);
      setTotal(res.total);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchGroups, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  const columns = useMemo<ColumnDef<GroupsItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('groups.table.header.group'),
        cell: ({ row }) => (
          <span
            style={{
              color: theme.palette.text.primary,
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.name ?? undefined}
          >
            {row.original.name ?? t('groups.empty_cell')}
          </span>
        ),
      },
      {
        accessorKey: 'assignedCourses',
        header: t('groups.table.header.assigned_courses'),
        cell: ({ row }) => (
          <span style={{ color: theme.palette.text.primary }}>
            {row.original.assignedCourses ?? t('groups.empty_cell')}
          </span>
        ),
      },
      {
        accessorKey: 'users',
        header: t('groups.table.header.users'),
        cell: ({ row }) => (
          <span style={{ color: theme.palette.text.primary }}>
            {row.original.users ?? t('groups.empty_cell')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('groups.table.header.actions'),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div
              style={{
                display: 'flex',
                gap: 0,
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {onEdit && (
                <button
                  type="button"
                  className="groups-btn-icon"
                  onClick={() => onEdit(item)}
                  aria-label={t('groups.edit_aria')}
                  data-e2e="groups-edit-button"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    padding: 0,
                  }}
                >
                  <img
                    src={iconEditTableGroups}
                    alt=""
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="groups-btn-icon"
                  onClick={() => onDelete(item)}
                  aria-label={t('groups.delete_aria')}
                  data-e2e="groups-delete-button"
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.palette.error.main,
                    padding: 0,
                  }}
                >
                  <img
                    src={iconDeleteTableGroups}
                    alt=""
                    width={ACTION_ICON_SIZE}
                    height={ACTION_ICON_SIZE}
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [theme, onEdit, onDelete, t]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount,
    state: { pagination },
    onPaginationChange: updater => {
      setPagination(prev =>
        typeof updater === 'function' ? updater(prev) : prev
      );
    },
  });

  const containerStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    fontFamily: theme.typography.fontFamily,
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xs,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize['2xl'],
    color: theme.palette.text.primary,
  };

  const subtitleStyle: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.secondary,
  };

  const tableWrapperStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.md,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    overflow: 'hidden',
    boxShadow: theme.layout.questionBankTable.tableCardShadow,
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
  };

  // Mesmo padrão do Users: manter a coluna de ações com largura fixa.
  // Além disso, ela pode expandir quando a tabela tem “sobras” de espaço.
  // Por isso adicionamos também `maxWidth` no `getThWidth`.
  const ACTION_COLUMN_WIDTH = 120;
  const ASSIGNED_COURSES_COLUMN_WIDTH = 170;
  const USERS_COLUMN_WIDTH = 90;
  // Usamos `minWidth` para permitir que a coluna de Group “absorva” o espaço restante.
  const GROUP_COLUMN_MAX_WIDTH = 220;

  const getThWidth = (columnId: string): React.CSSProperties => {
    if (columnId === 'name')
      return { minWidth: GROUP_COLUMN_MAX_WIDTH, width: 'auto' };
    if (columnId === 'actions')
      return {
        width: ACTION_COLUMN_WIDTH,
        minWidth: ACTION_COLUMN_WIDTH,
        maxWidth: ACTION_COLUMN_WIDTH,
      };
    if (columnId === 'assignedCourses')
      return {
        width: ASSIGNED_COURSES_COLUMN_WIDTH,
        minWidth: ASSIGNED_COURSES_COLUMN_WIDTH,
      };
    if (columnId === 'users')
      return { width: USERS_COLUMN_WIDTH, minWidth: USERS_COLUMN_WIDTH };
    return {};
  };

  const getThAlign = (columnId: string): React.CSSProperties => {
    if (columnId === 'actions') return { textAlign: 'center' as const };
    if (['assignedCourses', 'users'].includes(columnId)) {
      return { textAlign: 'center' as const };
    }
    return {};
  };

  const getTdWidth = (columnId: string): React.CSSProperties =>
    getThWidth(columnId);

  const getTdAlign = (columnId: string): React.CSSProperties => {
    if (columnId === 'actions') return { textAlign: 'center' as const };
    if (['assignedCourses', 'users'].includes(columnId)) {
      return { textAlign: 'center' as const };
    }
    return {};
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    fontSize: theme.typography.fontSize.base,
    borderBottom: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
  };

  const paginationStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  };

  const buttonClickStyle = `
    .groups-btn { transition: transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease, filter 0.12s ease; }
    .groups-btn:active { transform: scale(0.97); }
    .groups-btn-icon { transition: transform 0.12s ease, opacity 0.12s ease; }
    .groups-btn-icon:active { transform: scale(0.9); opacity: 0.8; }
  `;

  return (
    <div style={containerStyle} data-e2e="groups-list">
      <style>{buttonClickStyle}</style>
      <h1 style={headerStyle}>{titleText}</h1>
      <p style={subtitleStyle}>{subtitleText}</p>

      <SearchToolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={searchPlaceholderText}
        searchAriaLabel={t('groups.search_aria')}
        actionButton={
          onCreateGroup
            ? {
                label: createButtonLabelText,
                onClick: onCreateGroup,
                ariaLabel: createButtonLabelText,
                textColor: theme.palette.groups.createButtonText,
                backgroundColor: theme.palette.groups.createButtonBackground,
              }
            : undefined
        }
        classNamePrefix="groups"
        dataE2eSearchInput="groups-search-input"
        dataE2eActionButton="groups-create-button"
      />

      <div style={tableWrapperStyle}>
        {loading ? (
          <div
            style={{
              padding: theme.spacing['2xl'],
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            {t('groups.loading')}
          </div>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          ...thStyle,
                          ...getThWidth(header.column.id),
                          ...getThAlign(header.column.id),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  const groupId =
                    (row.original as Partial<GroupsItem>)?.id ?? undefined;
                  return (
                    <tr
                      key={row.id}
                      data-e2e={
                        groupId ? `groups-row-${groupId}` : 'groups-row'
                      }
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            ...tdStyle,
                            ...getTdWidth(cell.column.id),
                            ...getTdAlign(cell.column.id),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={paginationStyle}>
              <span
                style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.palette.text.secondary,
                }}
              >
                {t('groups.pagination.summary', {
                  count: data.length,
                  total,
                })}
              </span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                }}
              >
                <button
                  type="button"
                  className="groups-btn"
                  disabled={!table.getCanPreviousPage()}
                  onClick={() => table.previousPage()}
                  data-e2e="groups-pagination-prev"
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.secondary,
                    cursor: table.getCanPreviousPage()
                      ? 'pointer'
                      : 'not-allowed',
                    opacity: table.getCanPreviousPage() ? 1 : 0.6,
                  }}
                >
                  {t('groups.pagination.previous')}
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map(
                  pageNum => {
                    const isActive = pagination.pageIndex + 1 === pageNum;
                    return (
                      <button
                        key={pageNum}
                        type="button"
                        className="groups-btn"
                        onClick={() => table.setPageIndex(pageNum - 1)}
                        data-e2e={`groups-pagination-page-${pageNum}`}
                        style={{
                          minWidth: 32,
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          fontSize: theme.typography.fontSize.sm,
                          fontFamily: theme.typography.fontFamily,
                          border: isActive
                            ? 'none'
                            : `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          backgroundColor: isActive
                            ? theme.palette.primary.main
                            : theme.palette.background.paper,
                          color: isActive
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                          cursor: 'pointer',
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
                <button
                  type="button"
                  className="groups-btn"
                  disabled={!table.getCanNextPage()}
                  onClick={() => table.nextPage()}
                  data-e2e="groups-pagination-next"
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily,
                    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
                    borderRadius: theme.shape.borderRadius.sm,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    cursor: table.getCanNextPage() ? 'pointer' : 'not-allowed',
                    opacity: table.getCanNextPage() ? 1 : 0.6,
                  }}
                >
                  {t('groups.pagination.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
